import React, { useEffect } from 'react';
import { Handle, Position, NodeProps, NodeResizer, useUpdateNodeInternals } from 'reactflow';
import styled from 'styled-components';
import Icon from '@mdi/react';
import {
  mdiPump,
  mdiPipeDisconnected,
  mdiSourceBranch,
  mdiSourceMerge,
  mdiPipe,
  mdiFire,
  mdiSnowflake,
} from '@mdi/js';
import { CircuitNode as CircuitNodeType, ComponentResult } from '../types';
import { useUnits } from '../units';
import { DOMAIN_COLORS, getPortLayout } from '../services/ports';
import { useFluidNames } from '../services/coolant';
import { ALWAYS_SHOWN, pumpMode } from '../services/componentDefaults';

type ThresholdStatus = 'normal' | 'warning' | 'danger';

function getThresholdStatus(
  value: number | undefined,
  limit: number | undefined
): ThresholdStatus {
  if (value === undefined || limit === undefined) return 'normal';
  if (value > limit) return 'danger';
  if (value > limit - 5) return 'warning';
  return 'normal';
}

function worstStatus(a: ThresholdStatus, b: ThresholdStatus): ThresholdStatus {
  const order: ThresholdStatus[] = ['normal', 'warning', 'danger'];
  return order[Math.max(order.indexOf(a), order.indexOf(b))];
}

const BORDER_COLORS: Record<ThresholdStatus, string> = {
  normal: '#dadce0',
  warning: '#fbbc04',
  danger: '#ea4335',
};

const BG_TINTS: Record<ThresholdStatus, string> = {
  normal: '#ffffff',
  warning: '#fffde7',
  danger: '#fce8e6',
};

const NodeContainer = styled.div<{ selected: boolean; thresholdStatus: ThresholdStatus; $minHeight?: number }>`
  padding: 10px 14px;
  background: ${props => BG_TINTS[props.thresholdStatus]};
  border-radius: 6px;
  border: 2px solid ${props =>
    props.selected ? '#1a73e8' : BORDER_COLORS[props.thresholdStatus]};
  min-width: 110px;
  height: 100%;
  ${props => props.$minHeight ? `min-height: ${props.$minHeight}px;` : ''}
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    border-color: ${props =>
      props.selected ? '#1a73e8' :
      props.thresholdStatus !== 'normal' ? BORDER_COLORS[props.thresholdStatus] :
      '#bdc1c6'};
  }
`;

const NodeIconWrapper = styled.div<{ color: string }>`
  width: 32px;
  height: 32px;
  background-color: ${props => props.color};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
`;

const NodeLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #202124;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const NodeType = styled.div`
  font-size: 10px;
  color: #9aa0a6;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const CoolantTag = styled.div`
  margin-top: 3px;
  font-size: 10px;
  color: #1967d2;
  background: #e8f0fe;
  border-radius: 8px;
  padding: 0 6px;
  align-self: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
`;

const NodeProperties = styled.div`
  margin-top: 6px;
  font-size: 10px;
  color: #5f6368;
  line-height: 1.4;
`;

const NodeProperty = styled.div`
  background: #f1f3f4;
  border-radius: 2px;
  padding: 1px 5px;
  margin-bottom: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SimResults = styled.div`
  margin-top: 6px;
  font-size: 10px;
  line-height: 1.5;
`;

const TempBadge = styled.div<{ status: ThresholdStatus }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1px 5px;
  border-radius: 2px;
  margin-bottom: 1px;
  background: ${props =>
    props.status === 'danger' ? '#fce8e6' :
    props.status === 'warning' ? '#fef7e0' :
    '#e8f0fe'};
  color: ${props =>
    props.status === 'danger' ? '#c5221f' :
    props.status === 'warning' ? '#e37400' :
    '#1967d2'};
  font-weight: 500;
`;

const PortHandle = styled(Handle)<{ $color: string }>`
  width: 8px;
  height: 8px;
  background: ${props => props.$color};
  border: 2px solid white;
  box-shadow: 0 0 0 1px ${props => props.$color};

  &.react-flow__handle-valid {
    background: #1a73e8;
    box-shadow: 0 0 0 1px #1a73e8;
  }
`;

const PortDirectionMarker = styled.div<{ side: 'left' | 'right' }>`
  position: absolute;
  ${props => props.side === 'left' ? 'left: 6px' : 'right: 6px'};
  transform: translateY(-50%);
  font-size: 16px;
  color: #9aa0a6;
  pointer-events: none;
  line-height: 1;
`;

const ICON_MAP: Record<string, string> = {
  pump: mdiPump,
  pipe: mdiPipeDisconnected,
  branch: mdiSourceBranch,
  merge: mdiSourceMerge,
  restriction: mdiPipe,
  heat_source: mdiFire,
  cooling_package: mdiSnowflake,
};

const COLOR_MAP: Record<string, string> = {
  pump: '#1a73e8',
  pipe: '#5f6368',
  branch: '#34a853',
  merge: '#34a853',
  restriction: '#ea8600',
  heat_source: '#d93025',
  cooling_package: '#1a73e8',
};

const PROPERTY_LABELS: Record<string, string> = {
  flow_rate: 'Flow',
  inner_diameter_mm: 'ID',
  length_m: 'Length',
  heat_input_kw: 'Heat',
  k_factor: 'K',
  fixed_flow_lpm: 'Fixed Flow',
};

const PROPERTY_UNITS: Record<string, string> = {
  flow_rate: 'l/min',
  inner_diameter_mm: 'mm',
  length_m: 'm',
  heat_input_kw: 'kW',
  fixed_flow_lpm: 'l/min',
};

interface HydraulicNodeProps extends NodeProps {
  data: CircuitNodeType['data'] & {
    simulationResult?: ComponentResult;
  };
}

export const HydraulicNodeComponent: React.FC<HydraulicNodeProps> = ({
  id,
  data,
  selected,
}) => {
  const { component, custom_name, properties, display_properties, max_temperatures, simulationResult } = data;
  const units = useUnits();
  const fluidNames = useFluidNames();
  const updateNodeInternals = useUpdateNodeInternals();

  // Tell React Flow to recalculate handle positions when port count changes
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, component.input_ports, component.output_ports, component.ports, updateNodeInternals]);

  const iconPath = ICON_MAP[component.component_type] || mdiPipe;
  const iconColor = COLOR_MAP[component.component_type] || '#5f6368';

  // Determine threshold status from simulation results
  let thresholdStatus: ThresholdStatus = 'normal';
  let inletStatus: ThresholdStatus = 'normal';
  let outletStatus: ThresholdStatus = 'normal';

  if (simulationResult && max_temperatures) {
    inletStatus = getThresholdStatus(simulationResult.inlet_temperature, max_temperatures.inlet);
    outletStatus = getThresholdStatus(simulationResult.outlet_temperature, max_temperatures.outlet);
    thresholdStatus = worstStatus(inletStatus, outletStatus);
  }

  const isBranchOrMerge = component.component_type === 'branch' || component.component_type === 'merge';

  const renderPorts = () => {
    const ports = [];

    for (const port of getPortLayout(component)) {
      const color = DOMAIN_COLORS[port.domain];
      const title = [port.label || port.id, port.domain !== 'liquid' ? port.domain : '']
        .filter(Boolean).join(' · ');

      ports.push(
        <PortHandle
          key={port.id}
          type={port.handleType}
          position={port.side === 'left' ? Position.Left : Position.Right}
          id={port.id}
          $color={color}
          title={title}
          data-domain={port.domain}
          style={{ top: `${port.yPercent}%` }}
        />
      );
      ports.push(
        <PortDirectionMarker
          key={`${port.id}-marker`}
          side={port.side}
          style={{ top: `${port.yPercent}%`, color: port.domain === 'liquid' ? undefined : color }}
        >
          ▸
        </PortDirectionMarker>
      );
    }

    return ports;
  };

  // Key values are always on the tile (pump fixed flow, heat input); others
  // only when their "show on node" box is ticked in the properties panel.
  const alwaysShown = ALWAYS_SHOWN[component.component_type] || [];
  const facts: string[] = [];
  if (component.component_type === 'pump') {
    facts.push(pumpMode(properties) === 'fixed'
      ? `Fixed flow: ${units.displayWithUnit(properties.fixed_flow_lpm, 'flow')}`
      : 'Pump curve');
  }
  if (component.component_type === 'heat_source' && typeof properties?.heat_input_kw === 'number') {
    facts.push(`Heat: ${units.displayWithUnit(properties.heat_input_kw, 'power')}`);
  }
  const displayProps = properties && display_properties
    ? Object.entries(properties).filter(([key]) =>
        display_properties[key] && !alwaysShown.includes(key)
        && !(component.component_type === 'pump' && key === 'flow_rate'))
    : [];

  const displayName = custom_name || component.name;

  // Compute min-height for branch/merge nodes based on port count
  const layout = getPortLayout(component);
  const maxPorts = Math.max(
    layout.filter(p => p.side === 'left').length,
    layout.filter(p => p.side === 'right').length,
  );
  const nodeMinHeight = (isBranchOrMerge || component.ports?.length) && maxPorts > 1
    ? Math.max(80, maxPorts * 30)
    : undefined;

  return (
    <NodeContainer selected={selected || false} thresholdStatus={thresholdStatus} $minHeight={nodeMinHeight}>
      <NodeResizer
        isVisible={selected || false}
        minWidth={110}
        minHeight={nodeMinHeight || 60}
        handleStyle={{ width: 8, height: 8, borderRadius: 2 }}
        lineStyle={{ borderColor: '#1a73e8', borderWidth: 1 }}
      />
      {renderPorts()}
      <NodeIconWrapper color={iconColor}>
        <Icon path={iconPath} size={0.7} color="white" />
      </NodeIconWrapper>
      <NodeLabel title={displayName}>{displayName}</NodeLabel>
      <NodeType>{component.component_type_display}</NodeType>
      {component.component_type === 'pump' && properties?.fluid_id && fluidNames[properties.fluid_id] && (
        <CoolantTag title="Coolant of this circuit">{fluidNames[properties.fluid_id]}</CoolantTag>
      )}

      {(facts.length > 0 || displayProps.length > 0) && (
        <NodeProperties>
          {facts.map(text => <NodeProperty key={text} data-testid="node-fact">{text}</NodeProperty>)}
          {displayProps.map(([key, value]) => {
            const label = PROPERTY_LABELS[key] || key.replace(/_/g, ' ');
            const unit = PROPERTY_UNITS[key] || '';
            const displayValue = typeof value === 'number' ? value.toFixed(1) : String(value);
            return (
              <NodeProperty key={key}>
                {label}: {displayValue}{unit && ` ${unit}`}
              </NodeProperty>
            );
          })}
        </NodeProperties>
      )}

      {simulationResult && (
        <SimResults>
          <NodeProperty>
            {units.displayWithUnit(simulationResult.flow_rate, 'flow')}
          </NodeProperty>
          <TempBadge status={inletStatus}>
            <span>In:</span>
            <span>{units.displayWithUnit(simulationResult.inlet_temperature, 'temperature')}</span>
          </TempBadge>
          <TempBadge status={outletStatus}>
            <span>Out:</span>
            <span>{units.displayWithUnit(simulationResult.outlet_temperature, 'temperature')}</span>
          </TempBadge>
        </SimResults>
      )}
    </NodeContainer>
  );
};
